import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import UserAvatar from '../../../components/UserAvatar';

const UserTableRow = ({ user, onEdit, onDelete, onToggleStatus, onResetPassword, isSelected, onSelect, onToggleVoiceActor }) => {
  const [showActions, setShowActions] = useState(false);

  const getRoleBadgeColor = (role) => {
    const colors = {
      student: 'bg-accent/10 text-accent border-accent/20',
      admin: 'bg-secondary/10 text-secondary border-secondary/20',
      superadmin: 'bg-primary/10 text-primary border-primary/20'
    };
    return colors?.[role] || colors?.student;
  };

  const getStatusColor = (status) => {
    return status === 'active' ?'bg-success/10 text-success border-success/20' :'bg-muted text-muted-foreground border-border';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getActivityStatus = (lastLogin) => {
    const daysSinceLogin = Math.floor((new Date() - new Date(lastLogin)) / (1000 * 60 * 60 * 24));
    if (daysSinceLogin === 0) return { text: 'Today', color: 'text-success' };
    if (daysSinceLogin < 7) return { text: `${daysSinceLogin}d ago`, color: 'text-foreground' };
    if (daysSinceLogin < 30) return { text: `${Math.floor(daysSinceLogin / 7)}w ago`, color: 'text-muted-foreground' };
    return { text: 'Inactive', color: 'text-error' };
  };

  const activity = getActivityStatus(user?.lastLogin);

  const handleVoiceActorToggle = async (e) => {
    e?.stopPropagation();
    await onToggleVoiceActor?.(user?.id, e?.target?.checked);
  };

  return (
    <tr className="border-b border-border hover:bg-muted/50 transition-colors duration-150">
      <td className="px-4 py-3">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={(e) => onSelect(user?.id, e?.target?.checked)}
          className="w-4 h-4 rounded border-border text-primary focus:ring-2 focus:ring-primary focus:ring-offset-2"
          aria-label={`Select ${user?.name}`}
        />
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <UserAvatar userId={user?.id} name={user?.name} size="w-10 h-10" textSize="text-sm" />
          <div>
            <p className="font-medium text-foreground">{user?.name}</p>
            <p className="text-sm text-muted-foreground">{user?.email}</p>
          </div>
        </div>
      </td>
      <td className="px-4 py-3">
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${getRoleBadgeColor(user?.role)}`}>
          <Icon name={user?.role === 'student' ? 'GraduationCap' : user?.role === 'admin' ? 'Shield' : 'Crown'} size={14} />
          {user?.role?.charAt(0)?.toUpperCase() + user?.role?.slice(1)}
        </span>
      </td>
      <td className="px-4 py-3">
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusColor(user?.status)}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${user?.status === 'active' ? 'bg-success' : 'bg-muted-foreground'}`} />
          {user?.status?.charAt(0)?.toUpperCase() + user?.status?.slice(1)}
        </span>
      </td>
      <td className="px-4 py-3">
        <span className={`text-sm ${activity?.color}`}>{activity?.text}</span>
      </td>
      <td className="px-4 py-3 text-sm text-muted-foreground">
        {formatDate(user?.registeredDate)}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center justify-center">
          <Checkbox
            checked={user?.isVoiceActor || false}
            onChange={handleVoiceActorToggle}
            aria-label={`Mark ${user?.name} as voice actor`}
          />
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2 justify-end relative">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setShowActions(!showActions)}
            aria-label="More actions"
          >
            <Icon name="MoreVertical" size={18} />
          </Button>

          {showActions && (
            <>
              <div 
                className="fixed inset-0 z-10" 
                onClick={() => setShowActions(false)}
                aria-hidden="true"
              />
              <div className="absolute right-0 top-full mt-1 w-48 bg-popover border border-border rounded-md shadow-lg z-20 py-1">
                <button
                  className="w-full flex items-center gap-3 px-4 py-2 text-sm text-popover-foreground hover:bg-muted transition-colors duration-150"
                  onClick={() => {
                    onEdit(user);
                    setShowActions(false);
                  }}
                >
                  <Icon name="Edit" size={16} />
                  Edit User
                </button>
                <button
                  className="w-full flex items-center gap-3 px-4 py-2 text-sm text-popover-foreground hover:bg-muted transition-colors duration-150"
                  onClick={() => {
                    onToggleStatus(user?.id, user?.status === 'active' ? 'inactive' : 'active');
                    setShowActions(false);
                  }}
                >
                  <Icon name={user?.status === 'active' ? 'UserX' : 'UserCheck'} size={16} />
                  {user?.status === 'active' ? 'Deactivate' : 'Activate'}
                </button>
                <button
                  className="w-full flex items-center gap-3 px-4 py-2 text-sm text-popover-foreground hover:bg-muted transition-colors duration-150"
                  onClick={() => {
                    onResetPassword(user?.id);
                    setShowActions(false);
                  }}
                >
                  <Icon name="KeyRound" size={16} />
                  Reset Password
                </button>
                <div className="border-t border-border my-1" />
                <button
                  className="w-full flex items-center gap-3 px-4 py-2 text-sm text-error hover:bg-error/10 transition-colors duration-150"
                  onClick={() => {
                    onDelete(user?.id);
                    setShowActions(false);
                  }}
                >
                  <Icon name="Trash2" size={16} />
                  Delete User
                </button>
              </div>
            </>
          )}
        </div>
      </td>
    </tr>
  );
};

export default UserTableRow;